/**
 * Web "View individual suggestions": the twin of `HealthSuggestionList.tsx`.
 *
 * One hairline row per open suggestion: the audience symbol, the problem in
 * the user's words, the audience name under it, a chevron. A finding with a
 * proposal opens its editable comparison directly (`onOpenFinding`); the rest
 * (a question, a personal detail, a check-this-detail) expand in place with
 * their own actions, one at a time. Rows arrive with a short stagger.
 * `readOnly` (onboarding) draws every row plain: no chevron, nothing to press.
 */
import { useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { HealthAreaIcon } from './HealthAreaIcon';
import { HealthFindingRow } from './HealthFindingRow';
import {
  getAreaLabelKey,
  getFindingPlainFixKey,
  getFindingPlainKey,
  isFixableFinding,
} from './cv-health-copy';
import type { HealthSuggestionListProps } from './health-props';

export type { HealthSuggestionListProps };

export function HealthSuggestionList({
  report,
  findings,
  variant,
  onOpenFinding,
  onPreviewChange,
  onDecision,
  onAddProofLink,
  busyFindingKey,
  subtitle = 'area',
  readOnly = false,
}: HealthSuggestionListProps) {
  const { t } = useTranslation('common');
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  if (findings.length === 0) return null;

  return (
    <div className="tabbio-health tabbio-health-suggestions">
      {findings.map((finding, index) => {
        const style = { '--row-index': index } as CSSProperties;
        const areaLabel =
          subtitle === 'fix'
            ? // Only a change "Fix my CV" will make gets a "We'll…" line.
              t(isFixableFinding(finding) ? getFindingPlainFixKey(finding) : 'cvHealth.simple.yourCall')
            : t(getAreaLabelKey(finding.area));
        if (readOnly) {
          return (
            <div
              key={finding.key}
              className="tabbio-health-suggestion is-static"
              data-testid="cv-health-simple-row"
              style={style}
            >
              <span className="tabbio-health-suggestion-icon">
                <HealthAreaIcon area={finding.area} />
              </span>
              <span className="tabbio-health-suggestion-copy">
                <span className="tabbio-health-suggestion-title">{t(getFindingPlainKey(finding))}</span>
                <span className="tabbio-health-suggestion-sub">{areaLabel}</span>
              </span>
            </div>
          );
        }
        if (onOpenFinding && isFixableFinding(finding)) {
          return (
            <button
              key={finding.key}
              type="button"
              className="tabbio-health-suggestion"
              data-testid="cv-health-simple-row"
              style={style}
              onClick={() => onOpenFinding(finding)}
            >
              <span className="tabbio-health-suggestion-icon">
                <HealthAreaIcon area={finding.area} />
              </span>
              <span className="tabbio-health-suggestion-copy">
                <span className="tabbio-health-suggestion-title">{t(getFindingPlainKey(finding))}</span>
                <span className="tabbio-health-suggestion-sub">{areaLabel}</span>
              </span>
              <span className="tabbio-health-suggestion-chevron">
                <MaterialSymbol name="chevron_right" size={14} className="tabbio-rtl-flip" />
              </span>
            </button>
          );
        }
        return (
          <div key={finding.key} className="tabbio-health-suggestion-wrap" style={style}>
            <HealthFindingRow
              finding={finding}
              report={report}
              variant={variant}
              appearance="list"
              icon={<HealthAreaIcon area={finding.area} />}
              subtitle={areaLabel}
              expanded={expandedKey === finding.key}
              onToggle={() =>
                setExpandedKey((current) => (current === finding.key ? null : finding.key))
              }
              onPreviewChange={onPreviewChange}
              onDecision={onDecision}
              onAddProofLink={onAddProofLink}
              busyFindingKey={busyFindingKey}
            />
          </div>
        );
      })}
    </div>
  );
}

export default HealthSuggestionList;
